import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, Check, Search } from 'lucide-react';

/**
 * CustomSelect - Standardized, modern dropdown component for SST
 * 
 * @param {Array} options - Array of { value, label, disabled } or strings
 * @param {string|number} value - Currently selected value
 * @param {function} onChange - Callback receiving (selectedValue, optionObject)
 * @param {string} placeholder - Placeholder text when no value is selected
 * @param {string} label - Optional label displayed above the select
 * @param {string} width - CSS width (e.g. '100%', '200px')
 * @param {boolean} disabled - Whether the select is disabled
 * @param {boolean} searchable - Show search input inside dropdown (useful for long lists)
 * @param {string} size - 'sm' | 'md' | 'lg'
 * @param {string} variant - 'default' | 'filter' | 'pill'
 * @param {object} style - Additional styles for the wrapper
 */
const CustomSelect = ({
  options = [],
  value,
  onChange,
  placeholder = 'Select option',
  label,
  width = '100%',
  disabled = false,
  searchable = false,
  size = 'md',
  variant = 'default',
  dropUp: propDropUp,
  direction = 'auto', // 'auto' | 'up' | 'down'
  isOpen: controlledIsOpen,
  onOpenChange,
  style = {},
  className = '',
  buttonStyle = {},
  menuStyle = {},
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;

  const handleToggle = useCallback((nextVal) => {
    if (controlledIsOpen === undefined) {
      setInternalIsOpen(nextVal);
    }
    if (onOpenChange) {
      onOpenChange(nextVal);
    }
  }, [controlledIsOpen, onOpenChange]);

  const [searchTerm, setSearchTerm] = useState('');
  const [autoDropUp, setAutoDropUp] = useState(false);
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  const shouldDropUp = propDropUp !== undefined
    ? propDropUp
    : direction === 'up'
    ? true
    : direction === 'down'
    ? false
    : autoDropUp;

  useEffect(() => {
    if (isOpen && containerRef.current && propDropUp === undefined && direction === 'auto') {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      if (spaceBelow < 200 && spaceAbove > spaceBelow) {
        setAutoDropUp(true);
      } else {
        setAutoDropUp(false);
      }
    }
  }, [isOpen, propDropUp, direction]);

  // Normalize options into { value, label, disabled } format
  const normalizedOptions = options.map((opt) => {
    if (typeof opt === 'object' && opt !== null) {
      return {
        value: opt.value !== undefined ? opt.value : opt.id,
        label: opt.label !== undefined ? opt.label : (opt.name || opt.companyName || opt.title || String(opt.value)),
        disabled: !!opt.disabled,
      };
    }
    return { value: opt, label: String(opt), disabled: false };
  });

  // Current selected option
  const selectedOption = normalizedOptions.find((opt) => String(opt.value) === String(value));

  // Filter options if searchable
  const filteredOptions = searchable && searchTerm.trim()
    ? normalizedOptions.filter((opt) =>
        opt.label.toLowerCase().includes(searchTerm.toLowerCase().trim())
      )
    : normalizedOptions;

  // Handle outside click & escape key
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        handleToggle(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleToggle(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
      document.addEventListener('keydown', handleKeyDown);
      if (searchable && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, searchable, handleToggle]);

  const handleSelect = (option) => {
    if (option.disabled) return;
    if (onChange) {
      // Support both direct value and synthetic event
      onChange(option.value, option);
    }
    handleToggle(false);
    setSearchTerm('');
  };

  // Size configurations
  const SIZE_CONFIGS = {
    sm: {
      padding: '7px 12px',
      fontSize: '13px',
      iconSize: 14,
      borderRadius: '6px',
    },
    md: {
      padding: '10px 14px',
      fontSize: '14px',
      iconSize: 16,
      borderRadius: '8px',
    },
    lg: {
      padding: '12px 16px',
      fontSize: '15px',
      iconSize: 18,
      borderRadius: '8px',
    },
  };
  const sizeStyles = SIZE_CONFIGS[size] || SIZE_CONFIGS.md;

  // Variant configurations
  const isFilter = variant === 'filter';
  const isPill = variant === 'pill';

  return (
    <div
      ref={containerRef}
      className={`sst-custom-select-wrapper ${className}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        zIndex: isOpen ? 100 : 'auto',
        width,
        userSelect: 'none',
        ...style,
      }}
    >
      {label && (
        <label
          style={{
            fontSize: '12px',
            fontWeight: 600,
            color: '#374151',
            marginBottom: '6px',
            textTransform: 'uppercase',
            letterSpacing: '0.02em',
          }}
        >
          {label}
        </label>
      )}

      {/* Trigger Button */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && handleToggle(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: sizeStyles.padding,
          fontSize: sizeStyles.fontSize,
          borderRadius: isPill ? '24px' : sizeStyles.borderRadius,
          border: isOpen
            ? '1px solid #07A95B'
            : isFilter
            ? '1px solid #E5E7EB'
            : '1px solid #E5E7EB',
          backgroundColor: disabled ? '#F9FAFB' : '#FFFFFF',
          color: selectedOption ? '#1F2937' : '#9CA3AF',
          cursor: disabled ? 'not-allowed' : 'pointer',
          outline: 'none',
          boxShadow: isOpen
            ? '0 0 0 3px rgba(7, 169, 91, 0.14)'
            : '0 1px 2px 0 rgba(0, 0, 0, 0.03)',
          transition: 'all 0.18s ease-in-out',
          textAlign: 'left',
          boxSizing: 'border-box',
          ...buttonStyle,
        }}
        onMouseEnter={(e) => {
          if (!disabled && !isOpen && !buttonStyle.backgroundColor) {
            e.currentTarget.style.borderColor = '#9CA3AF';
            e.currentTarget.style.backgroundColor = '#FAFAFA';
          }
        }}
        onMouseLeave={(e) => {
          if (!disabled && !isOpen && !buttonStyle.backgroundColor) {
            e.currentTarget.style.borderColor = '#E5E7EB';
            e.currentTarget.style.backgroundColor = '#FFFFFF';
          }
        }}
      >
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontWeight: selectedOption ? 500 : 400,
            color: buttonStyle.color || (selectedOption ? '#1F2937' : '#9CA3AF'),
            flexGrow: 1,
            marginRight: '8px',
          }}
        >
          {selectedOption ? selectedOption.label : placeholder}
        </span>

        <ChevronDown
          size={sizeStyles.iconSize}
          style={{
            color: isOpen ? '#07A95B' : (buttonStyle.color || '#6B7280'),
            flexShrink: 0,
            transition: 'transform 0.2s ease, color 0.2s ease',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </button>

      {/* Floating Menu Popover */}
      {isOpen && (
        <div
          className="custom-dropdown-menu"
          style={{
            position: 'absolute',
            top: shouldDropUp ? 'auto' : 'calc(100% + 5px)',
            bottom: shouldDropUp ? 'calc(100% + 5px)' : 'auto',
            left: 0,
            right: 0,
            zIndex: 1000,
            backgroundColor: '#FFFFFF',
            borderRadius: '8px',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 8px 10px -6px rgba(0, 0, 0, 0.08)',
            border: '1px solid #E5E7EB',
            maxHeight: '260px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            ...menuStyle,
          }}
        >
          {/* Search Box if Searchable */}
          {searchable && (
            <div
              style={{
                padding: '8px 10px',
                borderBottom: '1px solid #F3F4F6',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: '#FAFAFA',
              }}
            >
              <Search size={14} color="#9CA3AF" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                style={{
                  border: 'none',
                  outline: 'none',
                  backgroundColor: 'transparent',
                  fontSize: '13px',
                  width: '100%',
                  color: '#1F2937',
                  fontFamily: 'inherit',
                }}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}

          {/* Options List */}
          <div
            style={{
              overflowY: 'auto',
              maxHeight: '220px',
              padding: '4px 0',
            }}
          >
            {filteredOptions.length === 0 ? (
              <div
                style={{
                  padding: '12px 16px',
                  fontSize: '13px',
                  color: '#9CA3AF',
                  textAlign: 'center',
                }}
              >
                No options found
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = String(option.value) === String(value);
                return (
                  <div
                    key={String(option.value)}
                    onClick={() => handleSelect(option)}
                    style={{
                      padding: '9px 14px',
                      fontSize: sizeStyles.fontSize,
                      cursor: option.disabled ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: isSelected ? '#ECFDF5' : 'transparent',
                      color: option.disabled
                        ? '#D1D5DB'
                        : isSelected
                        ? '#07A95B'
                        : '#374151',
                      fontWeight: isSelected ? 600 : 400,
                      transition: 'background-color 0.15s ease',
                      opacity: option.disabled ? 0.6 : 1,
                    }}
                    onMouseEnter={(e) => {
                      if (!option.disabled && !isSelected) {
                        e.currentTarget.style.backgroundColor = '#F9FAFB';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!option.disabled && !isSelected) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        marginRight: '8px',
                      }}
                    >
                      {option.label}
                    </span>
                    {isSelected && (
                      <Check size={15} color="#07A95B" style={{ flexShrink: 0 }} />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomSelect;
